import React, { useState, useEffect, useMemo } from 'react'
import { WaypointResponse } from '@/shared/api/routes'
import { useAuth } from '@/modules/auth'
import { useWaypointFolders } from '@/modules/routes/hooks/useWaypointFolders'
import { RoutesApi } from '@/shared/api/routes'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/ui/components'
import { FolderPlus } from '@/ui/icons'

interface AddWaypointToLibraryDialogProps {
  waypoint: WaypointResponse
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess?: () => void
}

export function AddWaypointToLibraryDialog({
  waypoint,
  open,
  onOpenChange,
  onSuccess
}: AddWaypointToLibraryDialogProps) {
  const { getAccessToken } = useAuth()
  const { folders, loading: foldersLoading, refreshFolders } = useWaypointFolders({ getAccessToken })
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null)
  const [isAdding, setIsAdding] = useState(false)

  const routesApi = useMemo(() => new RoutesApi(getAccessToken), [getAccessToken])

  // Find manually added folder (system folder with system_type = 'manual')
  const manuallyAddedFolder = useMemo(() => {
    const findManualFolder = (folderList: typeof folders): typeof folders[0] | null => {
      for (const folder of folderList) {
        if (folder.is_system && folder.system_type === 'manual') {
          return folder
        }
        if (folder.children && folder.children.length > 0) {
          const found = findManualFolder(folder.children)
          if (found) return found
        }
      }
      return null
    }
    return findManualFolder(folders)
  }, [folders])

  // Flatten folders for select dropdown (include nested folders)
  const flattenedFolders = useMemo(() => {
    const flatten = (folderList: typeof folders, depth = 0): Array<typeof folders[0] & { displayName: string }> => {
      const result: Array<typeof folders[0] & { displayName: string }> = []
      for (const folder of folderList) {
        const indent = '  '.repeat(depth)
        result.push({
          ...folder,
          displayName: `${indent}${folder.name}${folder.is_system ? ' (System)' : ''}`
        })
        if (folder.children && folder.children.length > 0) {
          result.push(...flatten(folder.children, depth + 1))
        }
      }
      return result
    }
    return flatten(folders)
  }, [folders])

  // Set default to manually added folder when dialog opens
  useEffect(() => {
    if (open && manuallyAddedFolder) {
      setSelectedFolderId(manuallyAddedFolder.id)
    } else if (open && !manuallyAddedFolder && !foldersLoading) {
      // If no manually added folder exists, we'll need to ensure system folders are created
      // For now, set to null and let the backend handle it
      setSelectedFolderId(null)
    }
  }, [open, manuallyAddedFolder, foldersLoading])

  // Ensure folders are loaded when dialog opens
  useEffect(() => {
    if (open && folders.length === 0 && !foldersLoading) {
      refreshFolders()
    }
  }, [open, folders.length, foldersLoading, refreshFolders])

  const handleAddToLibrary = async () => {
    // If no folder selected but manually added folder exists, use it
    let folderIdToUse = selectedFolderId || manuallyAddedFolder?.id || null

    // If no folder selected and no manually added folder exists, try refreshing folders once
    // The backend might create system folders on first folder API call
    if (!folderIdToUse && !manuallyAddedFolder && !foldersLoading) {
      try {
        await refreshFolders()
        // After refresh, check again for manually added folder
        // Note: This is a quick check, the folders state might not update immediately
        // but we'll proceed and let the backend handle validation
      } catch (refreshError) {
        // Continue anyway - backend will validate
      }
    }

    setIsAdding(true)

    try {
      // Update waypoint with folder_id
      // If folderIdToUse is null, the backend should handle it (though ideally it shouldn't be)
      await routesApi.updateWaypoint(waypoint.id, { folder_id: folderIdToUse }, '')

      if (onSuccess) {
        onSuccess()
      }
      
      onOpenChange(false)
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to add waypoint to library'
      
      // Provide more helpful error message if folder-related
      if (errorMessage.includes('folder') || errorMessage.includes('Folder')) {

      } else {

      }
    } finally {
      setIsAdding(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FolderPlus className="w-5 h-5" />
            Add Waypoint to Library
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div>
            <p className="text-sm text-gray-600 mb-2">
              Select a folder to save &quot;{waypoint.name}&quot; to your library:
            </p>
            
            {foldersLoading ? (
              <div className="text-sm text-gray-500 py-4">Loading folders...</div>
            ) : flattenedFolders.length === 0 ? (
              <div className="text-sm text-gray-500 py-4">
                No folders available. The waypoint will be added to the &quot;Manually Added&quot; folder.
              </div>
            ) : (
              <Select
                value={selectedFolderId || manuallyAddedFolder?.id || ''}
                onValueChange={(value) => setSelectedFolderId(value || null)}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select a folder" />
                </SelectTrigger>
                <SelectContent>
                  {manuallyAddedFolder && (
                    <SelectItem value={manuallyAddedFolder.id}>
                      {manuallyAddedFolder.name} (Default)
                    </SelectItem>
                  )}
                  {flattenedFolders
                    .filter(folder => folder.id !== manuallyAddedFolder?.id)
                    .map((folder) => (
                      <SelectItem key={folder.id} value={folder.id}>
                        {folder.displayName}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {manuallyAddedFolder && (
            <div className="text-xs text-gray-500 bg-gray-50 p-2 rounded">
              If no folder is selected, the waypoint will be added to &quot;{manuallyAddedFolder.name}&quot; by default.
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isAdding}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddToLibrary}
            disabled={isAdding || foldersLoading}
          >
            {isAdding ? 'Adding...' : 'Add to Library'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

